import { useCallback } from 'react';
import type { SetURLSearchParams } from 'react-router';
import { useSearchParams } from 'react-router';

import { parseLink } from './parseLink';
import type { Links, PaginationDirection } from './types';

export const usePaginationParams = ({ defaultPageSize = 20 }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const offsetParam = searchParams.get('offset');
  const offset = offsetParam ? parseInt(offsetParam, 10) : 0;

  const limitParam = searchParams.get('limit');
  const limit = limitParam ? parseInt(limitParam, 10) : defaultPageSize;

  return {
    offset,
    limit,
    searchParams,
    setSearchParams,
  };
};

export const usePaginationHandlers = ({
  offset,
  limit,
  totalCount = 0,
  searchParams,
  setSearchParams,
  links,
}: {
  offset: number;
  limit: number;
  totalCount?: number;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  links?: Links;
}) => {
  const from = offset + 1;
  const to = Math.min(offset + limit, totalCount);

  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + limit < totalCount;

  const handlePageClick = useCallback(
    (direction: PaginationDirection) => {
      const nextParams = new URLSearchParams(searchParams);
      switch (direction) {
        case 'first':
          nextParams.delete('offset');
          break;
        case 'prev':
          const prevOffset = parseLink('prev', links).offset;
          if (!prevOffset || prevOffset === '0') {
            nextParams.delete('offset');
            break;
          }

          nextParams.set('offset', prevOffset);
          break;
        case 'next':
          const nextOffset = parseLink('next', links).offset;
          if (nextOffset) {
            nextParams.set('offset', nextOffset);
          }
          break;
        case 'last':
          const lastOffset = parseLink('last', links).offset;
          if (lastOffset) {
            nextParams.set('offset', lastOffset);
          }
          break;
        default:
          break;
      }

      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams, links],
  );

  return {
    onFirstClick: hasPreviousPage ? () => handlePageClick('first') : undefined,
    onPreviousClick: hasPreviousPage ? () => handlePageClick('prev') : undefined,
    onNextClick: hasNextPage ? () => handlePageClick('next') : undefined,
    onLastClick: hasNextPage ? () => handlePageClick('last') : undefined,
    from,
    to,
  };
};
